export const ProductType = {
  WithVariants: 'with_variants',
  Simple: 'simple',
  Certificate: 'certificate',
} as const

export type BaseProduct = {
  id: Id
  code: Code
  name: string
  article?: string
  images: string[]
  section_id: string
  description?: Nullable<string>
  properties: Nullable<ProductProperties>
}
export type ProductWithVariants = BaseProduct & {
  type: 'with_variants'
} & {
  attributes: Nullable<ProductAttribute[]>
  variants: Nullable<ProductVariant[]>
}
export type ProductSimple = BaseProduct & {
  type: 'simple' | 'certificate'
  available: boolean
  stocks: ProductStock[]
  measurements: ProductMeasurement[]
  price: Nullable<ProductPrice>
}
export type Product = ProductWithVariants | ProductSimple
export type ProductDetail = Product & {
  section_id?: Id
  description?: string
}
export type ProductProperties = Record<string, ProductProperty>

export type ProductPropertySettings = {
  show_in_list: boolean
  show_in_card: boolean
}

export type ProductProperty = {
  title: string
  value: string
  settings: ProductPropertySettings
}
export type ProductAttributeValue = {
  title: string
  value: string
  disabled?: boolean
  active?: boolean
}
export type ProductAttribute = {
  code: Code
  name: string
  values: ProductAttributeValue[]
}
export type ProductPrice = {
  price: number
  price_formatted: string
  old_price: number
  old_price_formatted: string
  discount_percent: number
}
export type ProductStock = {
  warehouse_id: Id
  amount: number
}
export type ProductMeasurement = {
  weight: Nullable<number>
  width: Nullable<number>
  height: Nullable<number>
  length: Nullable<number>
}
export type ProductVariant = {
  id: Id
  name: string
  available: boolean
  price: ProductPrice
  stocks: ProductStock[]
  measurements: ProductMeasurement[]
  images: Product['images']
  attributes: Record<ProductAttribute['code'], ArrayItem<ProductAttribute['values']>['value']>
}

export function sortAttributeValues(
  attributeValues: (ProductAttributeValue & {available?: boolean; disabled?: boolean})[],
) {
  // Функция для разбора строки на компоненты
  function parseComponents(str: string) {
    const components = []
    let currentNumber = ''
    let currentText = ''

    for (let i = 0; i < str.length; i++) {
      const char = str[i]!

      if (/\d/.test(char)) {
        // Если текущий символ — цифра
        if (currentText) {
          components.push(currentText) // Сохраняем предыдущий текстовый блок
          currentText = ''
        }
        currentNumber += char
      } else {
        // Если текущий символ — не цифра
        if (currentNumber) {
          components.push(parseInt(currentNumber, 10)) // Сохраняем число
          currentNumber = ''
        }
        currentText += char
      }
    }

    // Добавляем оставшиеся компоненты
    if (currentNumber) components.push(parseInt(currentNumber, 10))
    if (currentText) components.push(currentText)

    return components
  }

  // Сортируем массив
  return attributeValues.sort((a, b) => {
    const componentsA = parseComponents(a.value)
    const componentsB = parseComponents(b.value)

    const maxLength = Math.max(componentsA.length, componentsB.length)

    for (let i = 0; i < maxLength; i++) {
      const partA = componentsA[i]
      const partB = componentsB[i]

      // Если одна из строк закончилась
      if (partA === undefined) return -1
      if (partB === undefined) return 1

      // Сравниваем числа
      if (typeof partA === 'number' && typeof partB === 'number') {
        if (partA !== partB) return partA - partB
      } else if (typeof partA === 'string' && typeof partB === 'string') {
        // Сравниваем строки
        if (partA < partB) return -1
        if (partA > partB) return 1
      } else {
        // Число всегда имеет приоритет над строкой
        if (typeof partA === 'number') return -1
        if (typeof partB === 'number') return 1
      }
    }

    return 0
  })
}
